import { Checkbox } from "@mui/material";
import { OrganizationEntityType, ClientEntityType, BankAccountEntityType, PatientEntityType, PractitionerEntityType } from "@/lib/supabase/supabaseTypes";
import { Button } from "../../../../components/ui/button";

interface PdfTestData {
	organization_id: string;
	organization: OrganizationEntityType;
	sample_clients: ClientEntityType[];
	sample_bank_account: BankAccountEntityType | null;
	sample_patient: PatientEntityType | null;
	sample_practitioner: PractitionerEntityType | null;
}

interface OrganizationSelectorProps {
	organizations: PdfTestData[];
	selectedIds: string[];
	onSelectionChange: (ids: string[]) => void;
}

export const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
	organizations,
	selectedIds,
	onSelectionChange,
}) => {
	const handleToggle = (orgId: string) => {
		const isSelected = selectedIds.includes(orgId);

		if (isSelected) {
			onSelectionChange(selectedIds.filter(id => id !== orgId));
		} else {
			onSelectionChange([...selectedIds, orgId]);
		}
	};

	const handleSelectAll = () => {
		const allIds = organizations.map(org => org.organization_id);
		onSelectionChange(allIds);
	};

	const handleClearAll = () => {
		onSelectionChange([]);
	};

	return (
		<div className="bg-white border border-gray-200 rounded-lg p-6">
			<div className="flex items-center justify-between mb-4">
				<h2 className="text-lg font-semibold text-gray-900">
					Organisationen auswählen ({selectedIds.length})
				</h2>
				<div className="flex gap-2">
					<Button
						onClick={handleSelectAll}
						variant="outline"
						size="sm"
					>
						Alle auswählen
					</Button>
					<Button
						onClick={handleClearAll}
						variant="outline"
						size="sm"
						disabled={selectedIds.length === 0}
					>
						Keine auswählen
					</Button>
				</div>
			</div>

			<div className="space-y-2 max-h-96 overflow-y-auto">
				{organizations.map((data) => {
					const isSelected = selectedIds.includes(data.organization_id);

					return (
						<div
							key={data.organization_id}
							className={`border rounded p-4 transition-colors ${
								isSelected
									? 'border-blue-500 bg-blue-50'
									: 'border-gray-200 hover:bg-gray-50 cursor-pointer'
							}`}
							onClick={() => handleToggle(data.organization_id)}
						>
							<div className="flex items-start gap-3">
								<Checkbox
									checked={isSelected}
									onChange={() => handleToggle(data.organization_id)}
									onClick={(e) => e.stopPropagation()}
								/>
								<div className="flex-1">
									<h3 className="font-medium text-gray-900">
										{data.organization.name || 'Unbenannt'}
									</h3>
									<p className="text-sm text-gray-600 mt-1">
										{data.organization.street}, {data.organization.postal_code} {data.organization.city}
									</p>
									<div className="flex gap-4 mt-2 text-xs">
										<span className={data.sample_clients.length > 0 ? 'text-green-600' : 'text-gray-400'}>
											Clients: {data.sample_clients.length}
										</span>
										<span className={data.sample_bank_account ? 'text-green-600' : 'text-gray-400'}>
											Bank: {data.sample_bank_account ? '✓' : '✗'}
										</span>
										<span className={data.sample_patient ? 'text-green-600' : 'text-gray-400'}>
											Patient: {data.sample_patient ? '✓' : '✗'}
										</span>
										<span className={data.sample_practitioner ? 'text-green-600' : 'text-gray-400'}>
											Practitioner: {data.sample_practitioner ? '✓' : '✗'}
										</span>
									</div>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};
